import React, { useState, useEffect } from 'react';
import { X, Gift, Clock, Sparkles, ArrowRight, Tag } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ModalSection = ({ config = {} }) => {
  const navigate = useNavigate();

  const {
    isShowModal = false,
    trigger = 'onMount',
    preset = 'split-image',

    // Overlay
    overlayColor = 'black',
    overlayOpacity = 65,
    overlayBlur = 'md',

    // Common
    title = 'Promotion Title',
    subtitle = 'Catchy description here',
    background = 'bg-yellow-400',
    textColor = '#111827',
    imageUrl = '',

    // Title / Subtitle styles
    titleStyle = { color: '#111827', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
    subtitleStyle = { color: '#111827', fontSize: 'text-xl', fontWeight: 'font-normal', fontFamily: 'font-sans' },

    // Button (main CTA)
    buttonText = 'Shop Now',
    buttonBg = 'from-purple-600 to-pink-600',
    buttonTextColor = '#ffffff',
    buttonStyle = { fontSize: 'text-lg', fontWeight: 'font-bold' },
    ctaHref = '/shop/all',           // ← NEW: destination route

    // Spin wheel extras
    discountCodePrefix = 'LUCKY',
    spinPrizes = ['10%', '15%', '20%', '25%', 'FREE SHIPPING'],
    wheelBackground = 'bg-white',
    wheelBorderColor = 'border-white/90',
    prizeEvenBg = 'bg-yellow-400 text-gray-900',
    prizeOddBg = 'bg-red-500 text-white',
    pointerColor = 'border-b-white',

    // Multi-product extras
    products = [
      { name: 'Summer Dress', price: '$45.99', old: '$89.99', img: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=400', disc: '50%' },
      { name: 'Designer Bag', price: '$79.99', old: '$159.99', img: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=400', disc: '50%' },
      { name: 'Sneakers', price: '$59.99', old: '$149.99', img: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400', disc: '60%' },
    ],

    // Dev container
    customStyles = { container: {} },
  } = config;

  const [isOpen, setIsOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [wonPrize, setWonPrize] = useState(null);

  useEffect(() => {
    if (!isShowModal) return;
    if (trigger === 'onMount') setIsOpen(true);
  }, [isShowModal, trigger]);

  const closeModal = () => setIsOpen(false);

  const handleSpin = () => {
    if (spinning) return;
    setSpinning(true);

    setTimeout(() => {
      setSpinning(false);
      const prize = spinPrizes[Math.floor(Math.random() * spinPrizes.length)];
      setWonPrize(prize);

      const code = `${discountCodePrefix}${prize.replace('%', '').replace(' ', '_').toUpperCase()}`;
      axios
        .post('/api/saveDiscount', { discount_code: code })
        .then(() => console.log(`Saved discount: ${code}`))
        .catch(err => console.error('Save failed', err));
    }, 3400);
  };

  if (!isShowModal || !isOpen) return null;

  // Container styles from dev mode
  const containerClass = customStyles.container?.className || '';
  const containerStyle = {
    padding: customStyles.container?.padding,
    zIndex: 50,
    margin: customStyles.container?.margin,
    borderRadius: customStyles.container?.borderRadius,
    boxShadow: customStyles.container?.boxShadow,
  };

  // Overlay dynamic
  const overlayBg = overlayColor.includes('rgba')
    ? overlayColor
    : `${overlayColor.startsWith('#') ? overlayColor : '#000000'}${Math.round((overlayOpacity / 100) * 255).toString(16).padStart(2, '0')}`;

  const overlayBlurClass = overlayBlur !== 'none' ? `backdrop-blur-${overlayBlur}` : '';

  const Overlay = () => (
    <div
      className={`fixed inset-0 z-30 transition-all duration-300 ${overlayBlurClass}`}
      style={{ backgroundColor: overlayBg }}
      onClick={closeModal}
    />
  );

  const baseModal = `fixed inset-0 z-50 flex items-center justify-center p-4 ${containerClass}`;
  const closeBtn = "absolute top-5 right-5 bg-white/90 backdrop-blur p-3 rounded-full shadow-lg hover:scale-110 transition z-10";

  const cardBgClass = background || 'bg-white'; // fallback

  // ── DESIGN 1 — Split Image ───────────────────────────────────────────
  if (preset === 'split-image') {
    return (
      <>
        <Overlay />
        <div className={baseModal} style={containerStyle}>
          <div className={`relative rounded-3xl bg-white overflow-hidden shadow-2xl max-w-4xl w-full grid md:grid-cols-2 animate-scaleIn ${cardBgClass}`}>
            <button onClick={closeModal} className={closeBtn}>
              <X className="w-6 h-6 text-gray-800" />
            </button>

            {imageUrl && (
              <div className="relative h-80 md:h-auto overflow-hidden">
                <img src={imageUrl} alt="Promo" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
              </div>
            )}

            <div className="p-10 md:p-14 flex flex-col justify-center" style={{ color: textColor }}>
              <h2 className={`${titleStyle.fontSize} ${titleStyle.fontWeight} ${titleStyle.fontFamily} mb-5 leading-tight`} style={{ color: titleStyle.color }}>
                {title}
              </h2>
              <p className={`${subtitleStyle.fontSize} ${subtitleStyle.fontWeight} ${subtitleStyle.fontFamily} mb-10 opacity-90`} style={{ color: subtitleStyle.color }}>
                {subtitle}
              </p>
              <button
                onClick={() => navigate(ctaHref || '/shop/all')}
                className={`w-full md:w-auto py-5 px-12 rounded-xl ${buttonStyle.fontSize} ${buttonStyle.fontWeight} shadow-xl transition hover:scale-105 flex items-center justify-center gap-3 ${buttonBg ? `bg-gradient-to-r ${buttonBg}` : 'bg-purple-600'}`}
                style={{ color: buttonTextColor }}
              >
                {buttonText} <ArrowRight className="w-6 h-6" />
              </button>
              <button onClick={closeModal} className="mt-6 text-sm opacity-70 hover:opacity-100">
                No thanks
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── DESIGN 2 — Centered Promo ────────────────────────────────────────
  if (preset === 'centered-promo') {
    return (
      <>
        <Overlay />
        <div className={baseModal} style={containerStyle}>
          <div className={`relative rounded-3xl overflow-hidden shadow-2xl max-w-2xl w-full text-center p-12 md:p-16 animate-bounceIn ${cardBgClass}`} style={{ color: textColor }}>
            <button onClick={closeModal} className={`${closeBtn} bg-white/20 hover:bg-white/40 text-gray-800`}>
              <X className="w-6 h-6" />
            </button>

            <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full text-sm font-bold mb-8 animate-pulse">
              <Gift className="w-5 h-5" /> FLASH SALE
            </div>

            <h2 className={`${titleStyle.fontSize} ${titleStyle.fontWeight} ${titleStyle.fontFamily} mb-6 drop-shadow-lg`} style={{ color: titleStyle.color }}>
              {title} 
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-yellow-200 to-white mt-2">{subtitle}</span>
            </h2>

            <button
              onClick={() => navigate(ctaHref || '/shop/all')}
              className={`mt-10 w-full py-6 rounded-xl ${buttonStyle.fontSize} ${buttonStyle.fontWeight} shadow-2xl transition hover:scale-105 ${buttonBg ? `bg-gradient-to-r ${buttonBg}` : 'bg-white text-red-600'}`}
              style={{ color: buttonTextColor || (buttonBg ? '#ffffff' : undefined) }}
            >
              {buttonText}
            </button>

            <p className="mt-6 text-sm opacity-80">Limited time — don't miss out!</p>
          </div>
        </div>
      </>
    );
  }

  // ── DESIGN 3 — Newsletter ────────────────────────────────────────────
  if (preset === 'newsletter') {
    return (
      <>
        <Overlay />
        <div className={baseModal} style={containerStyle}>
          <div className={`relative rounded-3xl shadow-2xl max-w-lg w-full text-center p-12 md:p-16 animate-slideUp ${cardBgClass}`} style={{ color: textColor }}>
            <button onClick={closeModal} className={`${closeBtn} bg-white/20 hover:bg-white/40 text-white`}>
              <X className="w-6 h-6" />
            </button>

            <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce">
              <Tag className="w-10 h-10" />
            </div>

            <h2 className={`${titleStyle.fontSize} ${titleStyle.fontWeight} ${titleStyle.fontFamily} mb-5`} style={{ color: titleStyle.color }}>
              {title}
            </h2>
            <p className={`${subtitleStyle.fontSize} ${subtitleStyle.fontWeight} ${subtitleStyle.fontFamily} mb-10 opacity-90`} style={{ color: subtitleStyle.color }}>
              {subtitle}
            </p>

            <div className="bg-white/15 backdrop-blur rounded-xl p-2 mb-6">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full bg-transparent text-white placeholder:text-white/60 px-6 py-4 focus:outline-none text-center font-medium"
              />
            </div>

            <button
              onClick={() => navigate(ctaHref || '/shop/all')}
              className={`w-full py-5 rounded-xl ${buttonStyle.fontSize} ${buttonStyle.fontWeight} shadow-xl transition hover:scale-105 ${buttonBg ? `bg-gradient-to-r ${buttonBg}` : 'bg-white text-blue-700'}`}
              style={{ color: buttonTextColor || (buttonBg ? '#ffffff' : undefined) }}
            >
              {buttonText}
            </button>

            <p className="mt-6 text-sm opacity-80">We respect your inbox — unsubscribe anytime</p>
          </div>
        </div>
      </>
    );
  }

  // ── DESIGN 4 — Spin the Wheel ────────────────────────────────────────
  if (preset === 'spin-wheel') {
    return (
      <>
        <Overlay />
        <div className={baseModal} style={containerStyle}>
          <div className={`relative rounded-3xl shadow-2xl max-w-lg w-full text-white overflow-hidden animate-scaleIn ${cardBgClass}`}>
            <button onClick={closeModal} className={`${closeBtn} bg-white/20 hover:bg-white/40 text-white`}>
              <X className="w-6 h-6" />
            </button>

            <div className="p-12 text-center">
              <h2 className={`${titleStyle.fontSize} ${titleStyle.fontWeight} ${titleStyle.fontFamily} mb-4 drop-shadow-lg`} style={{ color: titleStyle.color }}>
                🎯 {title} 🎯
              </h2>
              <p className={`${subtitleStyle.fontSize} ${subtitleStyle.fontWeight} ${subtitleStyle.fontFamily} mb-10 opacity-90`} style={{ color: subtitleStyle.color }}>
                {subtitle}
              </p>

              <div className="relative w-72 h-72 mx-auto mb-12">
                <div
                  className={`w-full h-full rounded-full border-8 ${wheelBorderColor} shadow-2xl overflow-hidden ${spinning ? 'animate-spin-wheel' : ''}`}
                >
                  <div className="grid grid-cols-2 h-full">
                    {spinPrizes.map((prize, i) => (
                      <div
                        key={i}
                        className={`flex items-center justify-center font-bold text-xl p-4 text-center ${i % 2 === 0 ? prizeEvenBg : prizeOddBg}`}
                      >
                        {prize}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-20">
                  <div className={`w-0 h-0 border-l-[28px] border-l-transparent border-r-[28px] border-r-transparent border-b-[48px] ${pointerColor} drop-shadow-xl`} />
                </div>

                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className={`w-24 h-24 ${wheelBackground} rounded-full flex items-center justify-center shadow-2xl text-4xl`}>🎁</div>
                </div>
              </div>

              {wonPrize ? (
                <div className="bg-white/25 backdrop-blur-lg rounded-2xl p-8 animate-bounce">
                  <h3 className="text-3xl font-bold mb-4">YOU WON!</h3>
                  <p className="text-5xl font-black text-yellow-300 mb-4">{wonPrize}</p>
                  <p className="text-lg">Code: <strong className="font-mono bg-white/40 px-4 py-2 rounded">{`${discountCodePrefix}${wonPrize.replace(/[% ]/g, '')}`}</strong></p>
                </div>
              ) : (
                <button
                  onClick={handleSpin}
                  disabled={spinning}
                  className={`w-full py-6 rounded-xl ${buttonStyle.fontSize} ${buttonStyle.fontWeight} shadow-2xl transition ${spinning ? 'bg-gray-400 cursor-not-allowed' : 'bg-white text-emerald-700 hover:scale-105'}`}
                >
                  {spinning ? 'SPINNING...' : buttonText || 'SPIN TO WIN!'}
                </button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── DESIGN 5 — Multi-Product Showcase ────────────────────────────────
  if (preset === 'multi-product') {
    return (
      <>
        <Overlay />
        <div className={baseModal} style={containerStyle}>
          <div className={`relative rounded-3xl overflow-hidden shadow-2xl max-w-5xl w-full animate-slideUp ${cardBgClass}`}>
            <button onClick={closeModal} className={closeBtn}>
              <X className="w-6 h-6 text-gray-800" />
            </button>

            <div className="p-8 md:p-12 text-center" style={{ color: textColor }}>
              <h2 className={`${titleStyle.fontSize} ${titleStyle.fontWeight} ${titleStyle.fontFamily} mb-4`} style={{ color: titleStyle.color }}>
                {title}
              </h2>
              <p className={`${subtitleStyle.fontSize} ${subtitleStyle.fontWeight} ${subtitleStyle.fontFamily} opacity-90`} style={{ color: subtitleStyle.color }}>
                {subtitle}
              </p>
            </div>

            <div className="p-8 md:p-12">
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {products.map((p, i) => (
                  <div key={i} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition hover:-translate-y-2">
                    <div className="relative h-56 overflow-hidden">
                      <img src={p.img || imageUrl} alt={p.name} className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                      {p.disc && (
                        <span className="absolute top-3 left-3 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                          -{p.disc}
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-lg mb-2">{p.name}</h3>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-xl font-bold text-pink-600">{p.price}</span>
                        {p.old && <span className="text-sm text-gray-400 line-through">{p.old}</span>}
                      </div>
                      <button className="w-full bg-gradient-to-r from-rose-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:opacity-90 transition">
                        Add to Cart
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate(ctaHref || '/shop/all')}
                className={`w-full py-5 rounded-xl ${buttonStyle.fontSize} ${buttonStyle.fontWeight} shadow-xl transition hover:scale-105 ${buttonBg ? `bg-gradient-to-r ${buttonBg}` : 'bg-purple-600'} text-white`}
                style={{ color: buttonTextColor }}
              >
                {buttonText} <ArrowRight className="inline ml-2" />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Fallback / minimal modal
  return (
    <>
      <Overlay />
      <div className={baseModal} style={containerStyle}>
        <div className={`relative rounded-3xl shadow-2xl max-w-md w-full text-center p-12 ${cardBgClass}`} style={{ color: textColor }}>
          <button onClick={closeModal} className={closeBtn}>
            <X className="w-6 h-6 text-gray-800" />
          </button>

          <h2 className={`${titleStyle.fontSize} ${titleStyle.fontWeight} ${titleStyle.fontFamily} mb-6`} style={{ color: titleStyle.color }}>
            {title || 'Welcome!'}
          </h2>
          <button 
            onClick={() => navigate(ctaHref || '/')}
            className="bg-blue-600 text-white px-10 py-4 rounded-xl font-bold"
          >
            {buttonText || 'Go to Shop'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ModalSection;